import type { Quote } from '@nowramp/sdk';
import { Spinner } from '../ui/Spinner';
import { InfoIcon, XIcon } from '../ui/icons';
import { useT } from '../../i18n';

export interface RateBreakdownProps {
  quote: Quote | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  countdown: number;
  quotesLoading: boolean;
}

export function RateBreakdown({
  quote,
  isOpen,
  onClose,
  countdown,
  quotesLoading,
}: RateBreakdownProps) {
  const t = useT();

  if (!isOpen) return null;

  const pad = (n: number) => String(n).padStart(2, '0');

  const fiatSymbol = quote?.fiatCurrency === 'USD' ? '$'
    : quote?.fiatCurrency === 'EUR' ? '\u20AC'
    : quote?.fiatCurrency === 'GBP' ? '\u00A3'
    : quote?.fiatCurrency ? `${quote.fiatCurrency} ` : '$';

  const formatFiat = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return `${fiatSymbol}0.00`;
    return `${fiatSymbol}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="px-5 py-5">
      {/* Header: countdown pill + info + X close */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {quotesLoading ? (
            <Spinner size={16} />
          ) : (
            <>
              <span className="text-[12px]" style={{ color: 'var(--t-text-muted)' }}>
                {t('rate.updatesInLabel')}
              </span>
              <span
                className="text-[12px] font-medium px-2 py-0.5 rounded-full"
                style={{ color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
              >
                00:{pad(countdown)}
              </span>
              <div className="relative group">
                <div
                  className="flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity cursor-help"
                  aria-label={t('rate.rateTooltip')}
                >
                  <InfoIcon size={12} />
                </div>
                <div
                  className="info-tooltip pointer-events-none absolute left-1/2 z-50 w-[200px] px-3 py-2 rounded-xl text-[11px] leading-snug font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ background: 'var(--t-tooltip-bg)', color: 'var(--t-tooltip-text)', top: 'calc(100% + 8px)', transform: 'translateX(-50%)' }}
                >
                  {t('rate.rateTooltip')}
                </div>
              </div>
            </>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center rounded-full transition-opacity hover:opacity-70"
          style={{ width: 28, height: 28, background: 'var(--t-surface)', color: 'var(--t-text)' }}
        >
          <XIcon size={10} />
        </button>
      </div>

      {!quote ? (
        <div className="text-[13px] py-4 text-center" style={{ color: 'var(--t-text-muted)' }}>
          {t('rate.noQuote')}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Large exchange rate value */}
          {quote.exchangeRate && (
            <div className="text-[28px] font-semibold" style={{ color: 'var(--t-text)' }}>
              {formatFiat(quote.exchangeRate)}
            </div>
          )}

          {/* Processing fee — stacked label + value */}
          {quote.fees?.processingFee && (
            <div>
              <span className="text-[13px] block mb-0.5" style={{ color: 'var(--t-text-secondary)' }}>
                {t('rate.processingFee')}{quote.fees.feePercentage ? ` ${t('rate.feeIncluded', { percentage: Math.round(parseFloat(String(quote.fees.feePercentage)) * 100) / 100 })}` : ''}
              </span>
              <span className="text-[15px] font-semibold" style={{ color: 'var(--t-text)' }}>
                {formatFiat(quote.fees.processingFee)}
              </span>
            </div>
          )}

          {/* Network fee — stacked label + value with crypto equivalent */}
          {quote.fees?.networkFee && (
            <div>
              <span className="flex items-center gap-1 text-[13px] mb-0.5" style={{ color: 'var(--t-text-secondary)' }}>
                {t('rate.networkFeeIncl')}
                <div className="relative group">
                  <div
                    className="flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity cursor-help"
                    aria-label={t('rate.networkFeeTooltip')}
                  >
                    <InfoIcon size={12} />
                  </div>
                  <div
                    className="info-tooltip pointer-events-none absolute left-1/2 z-50 w-[220px] px-3 py-2 rounded-xl text-[11px] leading-snug font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    style={{ background: 'var(--t-tooltip-bg)', color: 'var(--t-tooltip-text)', bottom: 'calc(100% + 8px)', transform: 'translateX(-50%)' }}
                  >
                    {t('rate.networkFeeTooltip')}
                  </div>
                </div>
              </span>
              <span className="text-[15px] font-semibold" style={{ color: 'var(--t-text)' }}>
                {formatFiat(quote.fees.networkFee)}
                {quote.exchangeRate && (
                  <span className="text-[13px] font-normal ml-1.5" style={{ color: 'var(--t-text-muted)' }}>
                    ({(parseFloat(quote.fees.networkFee) / parseFloat(quote.exchangeRate)).toFixed(8)} {quote.cryptoCurrency})
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
